import { supabase } from "@/integrations/supabase/client";
import type {
  CandidateProfile,
  GitHubData,
  LinkedInData,
  PortfolioData,
  ResumeData,
  VerifiedSkill,
} from "@/context/CandidateContext";

const emptyProfile: CandidateProfile = {
  verifiedSkills: [],
  resume: {
    rawText: null,
    summary: null,
    chronologicalExperience: [],
    education: [],
    certifications: [],
    awards: [],
    fileName: null,
    filePath: null,
  },
  github: {
    username: null,
    totalRepos: 0,
    topLanguages: [],
    stars: 0,
    followers: 0,
    pinnedRepos: [],
  },
  linkedin: {
    headline: null,
    location: null,
    connections: 0,
    endorsements: [],
    recommendations: [],
    profileUrl: null,
    rawText: null,
    pdfFileName: null,
  },
  portfolio: {
    cases: [],
    links: [],
  },
};

// Map a DB row from candidate_profiles to our CandidateProfile shape.
// We store resume sub-fields (summary/experience/education/certs) inside
// chronological_experience JSONB as { summary, experience, education, certifications }
// to avoid additional schema changes.
export function rowToProfile(row: any): CandidateProfile {
  if (!row) return emptyProfile;

  const verifiedSkills = (row.verified_skills as VerifiedSkill[]) ?? [];
  const resumeBlob = (row.chronological_experience as any) ?? {};
  const resume: ResumeData = {
    rawText: row.raw_resume_text ?? null,
    summary: resumeBlob.summary ?? null,
    chronologicalExperience: resumeBlob.experience ?? [],
    education: resumeBlob.education ?? [],
    certifications: resumeBlob.certifications ?? [],
    awards: Array.isArray(resumeBlob.awards) ? resumeBlob.awards : [],
    fileName: row.resume_file_name ?? null,
    filePath: row.resume_file_path ?? null,
  };

  const githubBlob = (row.github_metrics as any) ?? {};
  const github: GitHubData = {
    username: row.github_username ?? githubBlob.username ?? null,
    totalRepos: githubBlob.totalRepos ?? 0,
    topLanguages: githubBlob.topLanguages ?? [],
    stars: githubBlob.stars ?? 0,
    followers: githubBlob.followers ?? 0,
    pinnedRepos: githubBlob.pinnedRepos ?? [],
  };

  const liBlob = (row.linkedin_data as Record<string, any>) ?? {};
  const linkedin: LinkedInData = {
    headline: liBlob.headline ?? null,
    location: liBlob.location ?? null,
    connections: liBlob.connections ?? 0,
    endorsements: liBlob.endorsements ?? [],
    recommendations: liBlob.recommendations ?? [],
    profileUrl: liBlob.profileUrl ?? null,
    rawText: liBlob.rawText ?? null,
    pdfFileName: liBlob.pdfFileName ?? null,
  };

  const portfolioCases = (row.portfolio_cases as any[]) ?? [];
  const portfolioBlob = (row.portfolio_data as any) ?? {};
  const portfolio: PortfolioData = {
    cases: portfolioCases.length ? portfolioCases : (portfolioBlob.cases ?? []),
    links: portfolioBlob.links ?? [],
  };

  return { verifiedSkills, resume, github, linkedin, portfolio };
}

export function profileToRow(profile: CandidateProfile) {
  return {
    verified_skills: profile.verifiedSkills as any,
    chronological_experience: {
      summary: profile.resume.summary,
      experience: profile.resume.chronologicalExperience,
      education: profile.resume.education,
      certifications: profile.resume.certifications,
      awards: profile.resume.awards,
    } as any,
    raw_resume_text: profile.resume.rawText,
    resume_file_name: profile.resume.fileName,
    resume_file_path: profile.resume.filePath,
    github_username: profile.github.username,
    github_metrics: {
      username: profile.github.username,
      totalRepos: profile.github.totalRepos,
      topLanguages: profile.github.topLanguages,
      stars: profile.github.stars,
      followers: profile.github.followers,
      pinnedRepos: profile.github.pinnedRepos,
    } as any,
    portfolio_cases: profile.portfolio.cases as any,
    portfolio_data: { links: profile.portfolio.links } as any,
    linkedin_data: profile.linkedin as any,
  };
}

export const candidateQueryKey = (userId: string) => ["candidate", userId] as const;

export async function fetchCandidate(userId: string): Promise<CandidateProfile> {
  const { data, error } = await supabase
    .from("candidate_profiles")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return rowToProfile(data);
}

export async function upsertCandidate(userId: string, profile: CandidateProfile) {
  const row = profileToRow(profile);
  const { error } = await supabase
    .from("candidate_profiles")
    .update(row)
    .eq("user_id", userId);
  if (error) throw error;
}
